import React, { useState } from "react";

import { formatBytes, formatNumber, formatSqlObjectName } from "utils/format";

import SQL from "components/SQL";

import { IssueDetailQuery } from "../gql/Query.generated";

type IssueType = IssueDetailQuery["getIssue"];

import CopyToClipboard from "components/CopyToClipboard";
import { IssueReferenceTable } from "../../../../../docs/util/checks";
import { useRoutes } from "utils/routes";
import Panel from "components/Panel";

import { Link } from "react-router-dom";
import { ConfigSettingDocsSnippet } from "components/DocsSnippet";
import Tip from "components/Tip";
import Button from "components/Button";
import { ExpandToggle } from "components/Icons";
import Callout from "components/Callout";
import { DeadRowsGraph } from "components/VacuumGraph";
import Loading from "components/Loading";
import { useQuery } from "@apollo/client";
import moment from "moment";

import QUERY from "./Query.graphql";
import {
  SchemaTableStatsForVacuum,
  SchemaTableStatsForVacuumVariables,
} from "./types/SchemaTableStatsForVacuum";

type CurrentType = {
  growth_pct: number;
  row_growth: number;
  growth_bytes: number;
  estimated_pct_bloat: number;
  estimated_bloat_bytes: number;
  vacuum_count_by_dead_rows: number;
  autovacuum_vacuum_threshold: number;
  autovacuum_vacuum_scale_factor: number;
};

type RecommendationType = {
  growth_pct: number;
  row_growth: number;
  growth_bytes: number;
  vacuum_count_by_dead_rows: number;
  autovacuum_vacuum_threshold: number;
  autovacuum_vacuum_scale_factor: number;
};

const InsufficientVacuumFrequencyReferencesPanel: React.FunctionComponent<{
  issue: IssueType;
}> = ({ issue }) => {
  const issueDetails = issue.details;
  const current = issueDetails["current"] as CurrentType;
  const recommendation = issueDetails["recommendation"] as RecommendationType;
  const table = issue.references[0].referent as IssueReferenceTable;

  return (
    <>
      <Panel title="Guidance">
        <div className="flex m-4 gap-8">
          <SummaryCard
            current={current}
            recommendation={recommendation}
            table={table}
            databaseId={issue.databaseId}
          />
          <div className="min-w-max">
            <Recommendation
              current={current}
              recommendation={recommendation}
              serverId={issue.server.humanId}
            />
            <EstimatedImprovement
              current={current}
              recommendation={recommendation}
            />
          </div>
        </div>
        <div className="m-4">
          <Callout
            title="Insufficient VACUUM frequency typically results in table bloat"
            learnMoreLink="https://pganalyze.com/docs/vacuum-advisor/what-is-bloat"
          >
            Table bloat can cause slow queries, increased disk space, memory
            usage, and increased I/O.
          </Callout>
        </div>
      </Panel>
      <DeadRowsGraphWithRecommendedValue
        current={current}
        recommendation={recommendation}
        tableId={table.id}
        databaseId={issue.databaseId}
        endTs={issue.createdAt}
      />
    </>
  );
};

const SummaryCard: React.FunctionComponent<{
  current: CurrentType;
  recommendation: RecommendationType;
  table: IssueReferenceTable;
  databaseId: string;
}> = ({ current, recommendation, table, databaseId }) => {
  const { databaseTableVacuum } = useRoutes();

  const tableName = formatSqlObjectName(table.schemaName, table.tableName);
  const thresholdCurrent = current.autovacuum_vacuum_threshold;
  const thresholdRecommend = recommendation.autovacuum_vacuum_threshold;
  const scaleFactorCurrent = current.autovacuum_vacuum_scale_factor;
  const scaleFactorRecommend = recommendation.autovacuum_vacuum_scale_factor;
  const thresholdSql = `ALTER TABLE ${tableName} SET (autovacuum_vacuum_threshold = ${thresholdRecommend});`;
  const scaleFactorSql = `ALTER TABLE ${tableName} SET (autovacuum_vacuum_scale_factor = ${scaleFactorRecommend});`;

  const tableVacuumsUrl = databaseTableVacuum(databaseId, table.id);

  return (
    <div>
      {thresholdCurrent != thresholdRecommend && (
        <div className="rounded-[2px] px-2 py-1 bg-gray-100">
          <SQL className="text-base" inline sql={thresholdSql} />
          <div className="mt-2">
            <CopyToClipboard
              label="Copy ALTER TABLE command"
              content={thresholdSql}
            />
          </div>
        </div>
      )}
      {scaleFactorCurrent != scaleFactorRecommend && (
        <div className="rounded-[2px] px-2 py-1 bg-gray-100">
          <SQL className="text-base" inline sql={scaleFactorSql} />
          <div className="mt-2">
            <CopyToClipboard
              label="Copy ALTER TABLE command"
              content={scaleFactorSql}
            />
          </div>
        </div>
      )}
      <div className="pt-3">
        Avoidable table growth due to insufficient VACUUM frequency was
        detected. You can adjust the frequency of autovacuum by changing
        relevant config settings. Lowering{" "}
        <code>autovacuum_vacuum_threshold</code> or{" "}
        <code>autovacuum_vacuum_scale_factor</code> will increase the frequency
        of autovacuum in general.
      </div>
      <div className="pt-3">
        <HowToApplyDirections />
      </div>
      <div className="pt-3">
        <WhatToCheckDirections tableVacuumsUrl={tableVacuumsUrl} />
      </div>
      <div className="pt-3">
        To confirm the impact of changes, it is also recommended to{" "}
        <a
          href="https://pganalyze.com/blog/5mins-postgres-pg-repack-VACUUM-FULL"
          target="_blank"
          rel="noopener"
        >
          run pg_repack
        </a>{" "}
        to reclaim disk space. You can check out a graph of estimated bloat over
        time on the <Link to={tableVacuumsUrl}>VACUUM/ANALYZE Activity</Link>{" "}
        page.
      </div>
    </div>
  );
};

const Recommendation: React.FunctionComponent<{
  current: CurrentType;
  recommendation: RecommendationType;
  serverId: string;
}> = ({ current, recommendation, serverId }) => {
  const thresholdChanged =
    current.autovacuum_vacuum_threshold !==
    recommendation.autovacuum_vacuum_threshold;
  const scaleFactorChanged =
    current.autovacuum_vacuum_scale_factor !==
    recommendation.autovacuum_vacuum_scale_factor;

  return (
    <>
      <div className="text-[18px] text-[#555555] font-medium mb-1">
        Recommendation
      </div>
      <table className="leading-loose min-w-full">
        <tbody>
          <tr>
            <td></td>
            <td className="text-right pl-[20px]">Current</td>
            <td className="text-right pl-[20px]">Recommended</td>
          </tr>
          <tr className="border-b">
            <th className="font-normal">
              <ConfigSettingDocsSnippet
                serverId={serverId}
                configName="autovacuum_vacuum_threshold"
              />
            </th>
            <td className="text-right pl-[20px]">
              {formatNumber(current.autovacuum_vacuum_threshold)}
            </td>
            <td className="text-right pl-[20px]">
              <span className={`${thresholdChanged && "bg-yellow-200"}`}>
                {formatNumber(recommendation.autovacuum_vacuum_threshold)}
              </span>
            </td>
          </tr>
          <tr>
            <th className="font-normal">
              <ConfigSettingDocsSnippet
                serverId={serverId}
                configName="autovacuum_vacuum_scale_factor"
              />
            </th>
            <td className="text-right pl-[20px]">
              {formatNumber(current.autovacuum_vacuum_scale_factor, 2)}
            </td>
            <td className="text-right pl-[20px]">
              <span className={`${scaleFactorChanged && "bg-yellow-200"}`}>
                {formatNumber(recommendation.autovacuum_vacuum_scale_factor, 2)}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

const EstimatedImprovement: React.FunctionComponent<{
  current: CurrentType;
  recommendation: RecommendationType;
}> = ({ current, recommendation }) => {
  return (
    <>
      <div className="text-[18px] text-[#555555] font-medium mb-1 pt-5">
        Estimated Improvement
      </div>
      <table className="leading-loose min-w-full">
        <tbody>
          <tr>
            <td></td>
            <td className="text-right pl-[20px]">Current</td>
            <td className="text-right pl-[20px]">Estimate</td>
          </tr>
          <tr className="border-b">
            <th className="font-normal">
              Total autovacuum count{" "}
              <Tip content="Estimated total autovacuum count for the last 7 days, triggered by dead rows. It is expected to increase with recommended settings" />
            </th>
            <td className="text-right pl-[20px]">
              {formatNumber(current.vacuum_count_by_dead_rows)}
            </td>
            <td className="text-right pl-[20px]">
              {formatNumber(recommendation.vacuum_count_by_dead_rows)}
            </td>
          </tr>
          <tr className="border-b">
            <th className="font-normal" colSpan={3}>
              Avoidable growth{" "}
              <Tip content="Estimated table growth that could have been avoided if autovacuum was running more often" />
            </th>
          </tr>
          <tr className="border-b">
            <th className="font-normal pl-6">Rows</th>
            <td className="text-right pl-[20px]">
              {formatNumber(current.row_growth)}
            </td>
            <td className="text-right pl-[20px]">
              {formatNumber(recommendation.row_growth)}
            </td>
          </tr>
          <tr className="border-b">
            <th className="font-normal pl-6">
              Percentage{" "}
              <Tip content="Out of all new rows, how many contributed to avoidable growth" />
            </th>
            <td className="text-right pl-[20px]">
              {formatNumber(current.growth_pct, 1)}%
            </td>
            <td className="text-right pl-[20px]">
              {formatNumber(recommendation.growth_pct, 1)}%
            </td>
          </tr>
          <tr>
            <th className="font-normal pl-6">
              Bytes{" "}
              <Tip content="Average row size times avoidable rows growth" />
            </th>
            <td className="text-right pl-[20px]">
              {formatBytes(current.growth_bytes)}
            </td>
            <td className="text-right pl-[20px]">
              {formatBytes(recommendation.growth_bytes)}
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

const HowToApplyDirections: React.FunctionComponent = () => {
  const [expanded, setExpanded] = useState(false);
  const handleToggleExpanded = () => {
    setExpanded((currExp) => !currExp);
  };

  return (
    <>
      <Button bare onClick={handleToggleExpanded}>
        <ExpandToggle className="w-2 mr-1" expanded={expanded} />
        <span className="text-[18px] text-[#555555]">How to apply changes</span>
      </Button>
      {expanded && (
        <div>
          <p>
            In general, the <code>ALTER TABLE</code> command can be directly run
            within <code>psql</code>. Modifying these config settings is
            relatively safe, and you may need to make multiple changes until you
            find a value suitable for this table. Once the value is finalized,
            it is considered a best practice to include that{" "}
            <code>ALTER TABLE</code> command within a database migration script
            checked in with your source code.
          </p>
          <p>
            For a partitioned table or an auto-generated table, running an{" "}
            <code>ALTER TABLE</code> command as an one-off database migration is
            not ideal as you can only apply to the specific partitioned or
            auto-generated table. Usually, <code>CREATE TABLE</code> supports{" "}
            <code>WITH</code> clause to specify storage parameters like{" "}
            <code>autovacuum_vacuum_threshold</code>, which you can use for
            auto-generated tables. However, specifying these parameters for
            table partitions is not supported, so you'll need to run{" "}
            <code>ALTER TABLE</code> after new table partitions are created.
          </p>
        </div>
      )}
    </>
  );
};

const WhatToCheckDirections: React.FunctionComponent<{
  tableVacuumsUrl: string;
}> = ({ tableVacuumsUrl }) => {
  const [expanded, setExpanded] = useState(false);
  const handleToggleExpanded = () => {
    setExpanded((currExp) => !currExp);
  };

  return (
    <>
      <Button bare onClick={handleToggleExpanded}>
        <ExpandToggle className="w-2 mr-1" expanded={expanded} />
        <span className="text-[18px] text-[#555555]">
          What to check following ALTER TABLE
        </span>
      </Button>
      {expanded && (
        <div className="m-3">
          It is important to pay attention to the following points after making
          a change to these values and adjust further if needed:
          <ul className="ml-[20px]">
            <li>
              <h5>VACUUM frequency</h5>
              <p>
                Changing these settings is intended to increase the frequency of
                autovacuum. While insufficient vacuuming can cause bloat to
                accumulate and slow down queries, excessive vacuuming can impact
                the performance and stability of your database. You can check
                out recent autovacuums in{" "}
                <Link to={tableVacuumsUrl}>VACUUM/ANALYZE Activity</Link> page.
              </p>
            </li>
            <li>
              <h5>Bloat change</h5>
              <p>
                Typically, adjusting these settings won't eliminate existing
                bloat. Even with optimal settings, bloat that has already been
                accumulated cannot be eliminated unless the space occupied by
                dead rows becomes reusable via VACUUM, and there are more
                INSERTs than DELETEs to reuse that space.
              </p>
              <p>
                VACUUM FULL can also be used to eliminate bloat, but it requires
                heavyweight locks and must rewrite the entire table, making it
                impractical for most workloads.
              </p>
            </li>
          </ul>
        </div>
      )}
    </>
  );
};

const DeadRowsGraphWithRecommendedValue: React.FunctionComponent<{
  current: CurrentType;
  recommendation: RecommendationType;
  tableId: string;
  databaseId: string;
  endTs: number;
}> = ({ current, recommendation, tableId, databaseId, endTs }) => {
  const startTs = moment.unix(endTs).subtract(7, "day").unix();
  const { data, loading, error } = useQuery<
    SchemaTableStatsForVacuum,
    SchemaTableStatsForVacuumVariables
  >(QUERY, {
    variables: {
      databaseId,
      tableId,
      startTs,
      endTs,
    },
  });
  if (loading || error) {
    return (
      <Panel title="VACUUM Activity">
        <Loading error={!!error} />
      </Panel>
    );
  }
  return (
    <Panel title="VACUUM Activity">
      <DeadRowsGraph
        stats={data.getSchemaTableStats}
        autovacuumVacuumThreshold={current.autovacuum_vacuum_threshold}
        autovacuumVacuumScaleFactor={current.autovacuum_vacuum_scale_factor}
        altAutovacuumVacuumThreshold={
          recommendation.autovacuum_vacuum_threshold
        }
        altAutovacuumVacuumScaleFactor={
          recommendation.autovacuum_vacuum_scale_factor
        }
      />
    </Panel>
  );
};

export default InsufficientVacuumFrequencyReferencesPanel;
